<template>
  <div>
    <v-bottom-sheet
      v-if="bottomSheet"
      :model-value="activator"
      :persistent="persistent"
      :scrollable="scrollable"
      height="calc(100vh - 96px)"
      @update:modelValue="close"
    >
      <v-card flat>
        <v-card-title
          class="ra-blue-bg text-white py-1 text-no-wrap text-truncate d-flex"
          :style="{ backgroundColor: titleColor }"
        >
          <slot name="title">
            <RMessagesWrapper
              v-if="hasMessages"
              :model="model"
              :label="title"
              :parentPath="parentPath"
              :parentModel="parentModel"
              :checkParentProperty="checkParentProperty"
              scope="object"
            >
              <template #messagelessState>
                {{ title }}
              </template>
            </RMessagesWrapper>
            <template v-else>
              {{ title }}
            </template>
          </slot>
          <slot name="additionalTitleContent" />
          <v-spacer v-if="hasTitleSpacer" />
          <RMenu
            v-if="menuItems.length"
            :menuItems="menuItems"
            :model="model"
            color="white"
            isDark
          />
        </v-card-title>
        <v-progress-linear
          v-show="showProgressBar"
          color="#5278bb"
          height="3"
          indeterminate
        />
        <div class="content-container">
          <slot name="content" />
        </div>
        <v-card-actions class="pa-0">
          <v-footer height="48" border fixed>
            <slot name="actions">
              <v-spacer />
              <v-btn variant="text" @click="close">
                Close
              </v-btn>
            </slot>
          </v-footer>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
    <v-dialog
      v-else
      class="no-scroll"
      :model-value="activator"
      :height="height"
      :width="width"
      :max-width="maxWidth"
      :persistent="persistent"
      :scrollable="scrollable"
      :transition="transition"
      eager
      @update:modelValue="close"
    >
      <v-card :height="cardHeight" density="compact">
        <v-card-title class="ra-blue-bg text-white text-no-wrap text-truncate">
          <slot name="title">
            <RMessagesWrapper
              v-if="hasMessages"
              :model="model"
              :label="title"
              scope="object"
            >
              <template #messagelessState>
                {{ title }}
              </template>
            </RMessagesWrapper>
            <template v-else>
              {{ title }}
            </template>
          </slot>
          <slot name="additionalTitleContent" />
          <v-spacer />
          <RMenu
            v-if="menuItems.length"
            :menuItems="menuItems"
            :model="model"
            color="white"
            isDark
          />
        </v-card-title>
        <v-progress-linear
          v-show="showProgressBar"
          color="#5278bb"
          height="4"
          indeterminate
        />
        <slot name="content" />
        <slot name="actions" />
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { defineAsyncComponent } from 'vue';

/* istanbul ignore next */
const RMessagesWrapper = defineAsyncComponent(() => import('@/components/core/common/RMessagesWrapper.vue'));
/* istanbul ignore next */
const RMenu = defineAsyncComponent(() => import('@/components/core/common/RMenu.vue'));
/**
 * Generic Vuetify modal with two named slots, content and actions, for slotting in modal content
 * and actions. Title can be set via passing an additional prop.
 * @vue-prop {Boolean} activator - Current boolean state of v-dialog.
 * @vue-prop {String} [className = null] - Class name to lookup title against, if supplied.
 * @vue-prop {String} [title = ''] - Title to be used in place of meta data title.
 * @vue-prop {String|Number} [width = undefined] - Width of the v-dialog component.
 * @vue-prop {String|Number} [maxWidth = undefined] - Max width of the v-dialog component.
 * @vue-prop {Boolean} [persistent = false] - Clicking outside of the v-dialog
 * will not deactivate it.
 * @vue-prop {String} [cardHeight = ''] - Overall height of the v-card.
 * @vue-prop {String|Boolean} [transition = 'dialog-transition'] - Controls the type of transition
 * that occurs when a dialog is toggled.
 * @vue-prop {Boolean} [bottomSheet = false] - Displays modals in bottom-sheet mode.
 *
 * @vue-event {} toggle-dialog - Emitted when toggling state of modal in parent.
 */
export default {
  components: {
    RMessagesWrapper,
    RMenu,
  },

  props: {
    activator: {
      type: Boolean,
      required: true,
    },
    className: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    height: {
      type: [String, Number],
      default: undefined,
    },
    width: {
      type: [String, Number],
      default: undefined,
    },
    maxWidth: {
      type: [String, Number],
      default: undefined,
    },
    hasTitleSpacer: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    cardHeight: {
      type: String,
      default: '',
    },
    transition: {
      type: [String, Boolean],
      default: 'dialog-transition',
    },
    bottomSheet: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: true,
    },
    titleColor: {
      type: String,
      default: '',
    },
    showProgressBar: {
      type: Boolean,
      default: false,
    },
    hasMessages: {
      type: Boolean,
      default: false,
    },
    model: {
      type: [Object, Array],
      default: () => {},
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    parentPath: {
      type: String,
      default: '',
    },
    parentModel: {
      type: Object,
      default: () => {},
    },
    checkParentProperty: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['toggle-dialog'],

  data() {
    return {
      meta: null,
      localTitle: null,
    };
  },

  // REFACTOR: Potentially use mixin(s) for common logic (e.g. meta) or Vue-hooks
  created() {
    if (this.className) {
      this.meta = this.$store.getters['meta/class_schema'](this.className);
      this.localTitle = this.title || this.meta.labels.plural;
    }
  },

  methods: {
    /**
     * Called when clicking outside of a v-dialog.
     */
    close() {
      this.$emit('toggle-dialog');
    },
  },
};
</script>

<style lang="scss" scoped>
  .main-panel, .sidebar {
    overflow-y: auto;
  }

  .sidebar {
    max-height: 60vh;
  }

  .content-container {
    height: calc(100% - 88px);
  }

  .v-card-actions {
    min-height: 48px;
  }
</style>
